import type {
  PcrApplicant,
  PcrCreditRegisterExtract,
  PcrLoan,
  PcrYearlyIncome
} from "../../../types/pcr";
import {
  type CurrencyTotal,
  getCollateralTypes,
  getExtract,
  getIncomeData,
  getPcr,
  humanizePcrEnum,
  loanBalance,
  sumBalanceByCollateral
} from "../../../utils/pcr.helper";
import type { ExternalDataSourceProps } from "../types";
import {
  PcrApplicantHeader,
  PcrEmpty,
  PcrTable,
  PcrTableLabel,
  PcrTableWrapper
} from "./pcrStyles";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December"
];

const formatEur = (value: number | undefined, currency = "EUR"): string => {
  if (value === undefined || value === null) {
    return "-";
  }
  return new Intl.NumberFormat("fi-FI", {
    style: "currency",
    currency
  }).format(value);
};

// Renders one formatted amount per currency (e.g. "1 500,00 €"), joined when an
// extract spans several. Falls back to a zero EUR total when there are none.
const formatCurrencyTotals = (totals: CurrencyTotal[]): string => {
  if (totals.length === 0) {
    return formatEur(0);
  }
  return totals
    .map((total) => formatEur(total.sum, total.currencyCode))
    .join(", ");
};

const formatDate = (iso: string | undefined): string => {
  if (!iso) {
    return "-";
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }
  return new Intl.DateTimeFormat("fi-FI").format(date);
};

const CreditSummary = ({ extract }: { extract: PcrCreditRegisterExtract }) => {
  const summary = extract.creditInformationSummary;
  const loans = extract.loans ?? [];

  const withCollateral = sumBalanceByCollateral(loans, true);
  const withoutCollateral = sumBalanceByCollateral(loans, false);

  const lastRepayments = summary?.repaymentsPaidLastAmount ?? [];

  const rows: { label: string; value: string }[] = [
    { label: "Lenders", value: String(summary?.lendersCount ?? "-") },
    {
      label: "Loan contracts",
      value: String(summary?.loanContractsCount ?? "-")
    },
    {
      label: "Guaranteed loan contracts",
      value: String(summary?.guaranteedLoanContractsCount ?? "-")
    },
    {
      label: "Balance with collateral",
      value: formatCurrencyTotals(withCollateral)
    },
    {
      label: "Balance without collateral",
      value: formatCurrencyTotals(withoutCollateral)
    },
    {
      label: "Last repayments paid",
      value:
        lastRepayments.length > 0 ? formatCurrencyTotals(lastRepayments) : "-"
    }
  ];

  return (
    <>
      <PcrTableLabel>Credit summary</PcrTableLabel>
      <PcrTableWrapper>
        <PcrTable>
          <tbody>
            {rows.map((row) => (
              <tr key={row.label}>
                <th scope="row">{row.label}</th>
                <td className="numeric">{row.value}</td>
              </tr>
            ))}
          </tbody>
        </PcrTable>
      </PcrTableWrapper>
    </>
  );
};

const LoansTable = ({ loans }: { loans: PcrLoan[] }) => {
  if (loans.length === 0) {
    return <PcrEmpty>No loans reported</PcrEmpty>;
  }

  return (
    <>
      <PcrTableLabel>Loans ({loans.length})</PcrTableLabel>
      <PcrTableWrapper>
        <PcrTable>
          <thead>
            <tr>
              <th>Loan Type</th>
              <th>Collateral</th>
              <th>Collateral Type</th>
              <th>Purpose of Use</th>
              <th>Contract Date</th>
              <th className="numeric">Amount Paid</th>
              <th className="numeric">Balance</th>
              <th className="numeric">Borrowers</th>
              <th>Repayment</th>
              <th className="numeric">Late Payments</th>
            </tr>
          </thead>
          <tbody>
            {loans.map((loan, index) => {
              const currency = loan.currencyCode || "EUR";
              return (
                <tr key={`${loan.loanType}-${loan.contractDate}-${index}`}>
                  <td>{humanizePcrEnum(loan.loanType)}</td>
                  <td>{loan.isLoanWithCollateral ? "Yes" : "No"}</td>
                  <td>
                    {getCollateralTypes(loan)
                      .map((type) => humanizePcrEnum(type))
                      .join(", ") || "-"}
                  </td>
                  <td>{humanizePcrEnum(loan.lumpSumLoan?.purposeOfUse)}</td>
                  <td>{formatDate(loan.contractDate)}</td>
                  <td className="numeric">
                    {formatEur(loan.lumpSumLoan?.amountPaid, currency)}
                  </td>
                  <td className="numeric">
                    {formatEur(loanBalance(loan), currency)}
                  </td>
                  <td className="numeric">{loan.borrowersCount ?? "-"}</td>
                  <td>{humanizePcrEnum(loan.lumpSumLoan?.repaymentMethod)}</td>
                  <td className="numeric">{loan.delayedAmount?.length ?? 0}</td>
                </tr>
              );
            })}
          </tbody>
        </PcrTable>
      </PcrTableWrapper>
    </>
  );
};

const IncomeYearTable = ({ year }: { year: PcrYearlyIncome }) => {
  const months = [...year.months].sort((a, b) => a.month - b.month);

  return (
    <>
      <PcrTableLabel>Income {year.year}</PcrTableLabel>
      <PcrTableWrapper>
        <PcrTable>
          <thead>
            <tr>
              <th>Month</th>
              <th className="numeric">Wage (Gross)</th>
              <th className="numeric">Wage (Net)</th>
              <th className="numeric">Benefits (Gross)</th>
              <th className="numeric">Benefits (Net)</th>
            </tr>
          </thead>
          <tbody>
            {months.map((month) => (
              <tr key={month.month}>
                <td>{MONTHS[month.month - 1] ?? month.month}</td>
                <td className="numeric">{formatEur(month.wagesGrossAmount)}</td>
                <td className="numeric">{formatEur(month.wagesNetAmount)}</td>
                <td className="numeric">
                  {formatEur(month.benefitsGrossAmount)}
                </td>
                <td className="numeric">
                  {formatEur(month.benefitsNetAmount)}
                </td>
              </tr>
            ))}
          </tbody>
        </PcrTable>
      </PcrTableWrapper>
    </>
  );
};

const PcrApplicantBlock = ({
  title,
  applicant
}: {
  // Omitted for a single applicant — the accordion title already names the source.
  title?: string;
  applicant: PcrApplicant | undefined;
}) => {
  const extract = getExtract(applicant);
  if (!extract) {
    return null;
  }

  const incomeData = getIncomeData(applicant);
  const years = [...incomeData].sort((a, b) => b.year - a.year);

  return (
    <div>
      {title && <PcrApplicantHeader>{title}</PcrApplicantHeader>}
      <CreditSummary extract={extract} />
      <LoansTable loans={extract.loans ?? []} />
      {years.length === 0 ? (
        <PcrEmpty>No income data reported</PcrEmpty>
      ) : (
        years.map((year) => <IncomeYearTable key={year.year} year={year} />)
      )}
    </div>
  );
};

export const PcrSection = ({ data }: ExternalDataSourceProps) => {
  const pcr = getPcr(data);
  const hasApplicant = Boolean(getExtract(pcr?.applicant));
  const hasCoapplicant = Boolean(getExtract(pcr?.coapplicant));

  return (
    <div>
      <PcrApplicantBlock
        title={hasCoapplicant ? "Applicant" : undefined}
        applicant={pcr?.applicant}
      />
      {hasCoapplicant && (
        <PcrApplicantBlock title="Co-applicant" applicant={pcr?.coapplicant} />
      )}
      {!hasApplicant && !hasCoapplicant && (
        <PcrEmpty>No PCR data available</PcrEmpty>
      )}
    </div>
  );
};
